import React, { useEffect, useMemo } from "react";
import { useGetOrganizationByCodeQuery } from "../../store/apis/organization.api";
import { useDispatch } from "react-redux";
import { setWeekOffConfig } from "../../store/slices/authSlice";

export function getOrgCodeFromSubdomain(): string | null {
  const hostname = window.location.hostname;
  const match = hostname.match(/^(.+)-hrms\./);
  return match ? match[1] : null;
}

export const DEFAULT_ORG = {
  name: "THE STACKMENTALIST",
  tagline: "Great Innovations Ahead",
  queryCode: "STK",
} as const;

function getInitials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase())
    .slice(0, 2)
    .join("");
}

const OrgLogo: React.FC<{
  logoUrl?: string | null;
  name: string;
  loading?: boolean;
}> = ({ logoUrl, name, loading }) => {
  const initials = getInitials(name);

  if (loading) {
    return (
      <div className="h-10 w-10 shrink-0 animate-pulse rounded-md bg-[var(--color-inverse-overlay)]" />
    );
  }

  if (logoUrl) {
    return (
      <img
        src={logoUrl}
        alt={name}
        className="h-10 w-10 shrink-0 rounded-md object-cover"
      />
    );
  }

  if (initials) {
    return (
      <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md bg-[var(--color-inverse-overlay-strong)]">
        <span className="text-label font-extrabold text-[var(--color-text-inverse)]">
          {initials}
        </span>
      </div>
    );
  }

  return null;
};

const AuthLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const orgCode = useMemo(() => getOrgCodeFromSubdomain(), []);
  const queryCode = orgCode || DEFAULT_ORG.queryCode;
  const dispatch = useDispatch();
  const { data: orgData, isLoading: orgLoading } =
    useGetOrganizationByCodeQuery(queryCode);

  useEffect(() => {
    if (orgData?.weekOffConfig) {
      dispatch(setWeekOffConfig(orgData.weekOffConfig));
    }
  }, [orgData?.weekOffConfig, dispatch]);

  const displayName =
    orgData?.name || orgCode?.toUpperCase() || DEFAULT_ORG.name;
  const logoUrl = orgData?.logo;
  const tagline = orgData?.tagline ?? DEFAULT_ORG.tagline;
  const year = new Date().getFullYear();

  return (
    <div className="flex min-h-screen w-full flex-col bg-surface">
      <div className="flex flex-1 flex-col lg:flex-row">
        {/* Brand panel — gradient on desktop, compact header on mobile */}
        <aside className="bg-brand-panel relative flex flex-col justify-between gap-6 overflow-hidden px-6 py-7 lg:w-[52%] lg:gap-10 lg:px-12 lg:py-12">
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -right-[120px] -top-[140px] h-[420px] w-[420px] rounded-full bg-[var(--color-inverse-overlay-soft)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute -bottom-[110px] -left-[90px] h-[300px] w-[300px] rounded-full bg-[var(--color-inverse-overlay-soft)]"
          />
          <div
            aria-hidden="true"
            className="pointer-events-none absolute bottom-[120px] right-20 hidden h-[180px] w-[180px] rounded-full border border-[var(--color-inverse-border)] lg:block"
          />

          {/* Organization identity */}
          <div className="relative flex items-center gap-3">
            <OrgLogo
              logoUrl={logoUrl}
              name={displayName}
              loading={orgLoading}
            />
            <div className="min-w-0">
              {displayName && (
                <p className="text-brand-title truncate text-[var(--color-text-inverse)]">
                  {displayName}
                </p>
              )}
              {tagline && (
                <p className="text-caption truncate font-medium text-[var(--color-text-inverse-subtle)]">
                  {tagline}
                </p>
              )}
            </div>
          </div>

          {/* Editorial block — desktop only */}
          <div className="relative hidden max-w-[440px] lg:block">
            <p className="text-caption mb-3 uppercase text-[var(--color-text-inverse-subtle)]">
              Welcome to
            </p>
            <h2 className="text-display-lg text-[var(--color-text-inverse)]">
              Manage your people, beautifully.
            </h2>
            <p className="text-body-lg mt-5 text-[var(--color-text-inverse-muted)]">
              Attendance, leave, performance and the entire employee lifecycle
              — brought together in one calm, modern workspace.
            </p>
          </div>

          <p className="text-body-sm relative hidden text-[var(--color-text-inverse-subtle)] lg:block">
            © {year} {displayName} HR Suite
          </p>
        </aside>

        {/* Form panel */}
        <main className="flex flex-1 items-center justify-center bg-surface px-5 py-10 sm:px-8 lg:px-10 lg:py-12">
          <div className="w-full max-w-[384px]">{children}</div>
        </main>
      </div>
    </div>
  );
};

export default AuthLayout;
